import { useState, useEffect, useRef } from "react";
import Player from "@vimeo/player";
import { Play, Pause, Volume2 } from "lucide-react";

interface VimeoVideoCardProps {
  vimeoId: string;
  fallbackTitle?: string;
  fluid?: boolean;
  forceTitle?: boolean;
}

export default function VimeoVideoCard({ vimeoId, fallbackTitle, fluid, forceTitle }: VimeoVideoCardProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [meta, setMeta] = useState<{ title: string; thumbnail: string } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const currentTimeRef = useRef(0);
  const activatingAudioRef = useRef(false);
  const audioRetryTimersRef = useRef<number[]>([]);

  const clearAudioRetryTimers = () => {
    audioRetryTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    audioRetryTimersRef.current = [];
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}&width=640`)
            .then((r) => r.json())
            .then((data) => {
              setMeta({
                title: forceTitle && fallbackTitle ? fallbackTitle : (data.title || fallbackTitle || "Vídeo"),
                thumbnail: data.thumbnail_url || "",
              });
            })
            .catch(() => {
              setMeta({ title: fallbackTitle || "Vídeo", thumbnail: "" });
            });
        }
      },
      { rootMargin: "400px 600px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [vimeoId, fallbackTitle, forceTitle]);

  const handleTogglePlay = () => {
    setPlaying((p) => {
      if (p) {
        clearAudioRetryTimers();
        activatingAudioRef.current = false;
        setMuted(true);
        currentTimeRef.current = 0;
      }
      return !p;
    });
  };

  useEffect(() => {
    if (!playing || !iframeRef.current) {
      playerRef.current?.destroy().catch(() => undefined);
      playerRef.current = null;
      return;
    }

    const player = new Player(iframeRef.current);
    playerRef.current = player;

    const handleTimeUpdate = (data: { seconds: number }) => {
      currentTimeRef.current = data.seconds;
    };

    player.on("timeupdate", handleTimeUpdate);

    player.ready()
      .then(async () => {
        await player.setAutopause(false).catch(() => undefined);
        await player.setMuted(true).catch(() => undefined);
        await player.play().catch(() => undefined);
      })
      .catch(() => undefined);

    return () => {
      clearAudioRetryTimers();
      activatingAudioRef.current = false;
      player.off("timeupdate", handleTimeUpdate);
      player.destroy().catch(() => undefined);
      if (playerRef.current === player) playerRef.current = null;
    };
  }, [playing, vimeoId]);

  const activateAudio = () => {
    if (activatingAudioRef.current) return;

    const player = playerRef.current;
    setMuted(false);

    if (!player) {
      activatingAudioRef.current = false;
      return;
    }

    activatingAudioRef.current = true;
    clearAudioRetryTimers();

    const sendDirectCommand = (method: string, value?: unknown) => {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify(value === undefined ? { method } : { method, value }),
        "https://player.vimeo.com"
      );
    };

    const enableSoundAndKeepPlaying = () => {
      sendDirectCommand("setMuted", false);
      sendDirectCommand("setVolume", 1);
      sendDirectCommand("play");

      void player.setMuted(false).catch(() => undefined);
      void player.setVolume(1).catch(() => undefined);
      void player.play().catch(() => undefined);
    };

    enableSoundAndKeepPlaying();

    [200, 650, 1200].forEach((delay) => {
      const timer = window.setTimeout(enableSoundAndKeepPlaying, delay);
      audioRetryTimersRef.current.push(timer);
    });

    const verificationTimer = window.setTimeout(() => {
      void player.getPaused()
        .then((paused) => {
          if (paused) enableSoundAndKeepPlaying();
        })
        .catch(() => undefined)
        .finally(() => {
          activatingAudioRef.current = false;
        });
    }, 1800);

    audioRetryTimersRef.current.push(verificationTimer);
  };

  const handleUnmute = (e: React.PointerEvent<HTMLButtonElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    activateAudio();
  };

  const embedUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&autopause=0&controls=0&unmute_button=0&transparent=0&title=0&byline=0&portrait=0&playsinline=1&dnt=1&app_id=122963`;

  return (
    <div
      ref={ref}
      className={`video-card relative rounded-2xl shadow-xl snap-center group cursor-pointer border border-white/10 hover:border-white/30 transition-all duration-500 bg-slate-800 overflow-hidden isolate ${fluid ? "w-full h-full border-slate-200" : "shrink-0 w-[200px] md:w-[250px] aspect-9-16 hover:-translate-y-1.5"}`}
      onClick={!playing ? handleTogglePlay : undefined}
    >
      {playing ? (
        <>
          <iframe
            ref={iframeRef}
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            title={meta?.title || "Vimeo video"}
            width={250}
            height={444}
            loading="lazy"
          />
          {muted && (
            <button
              type="button"
              onPointerDown={handleUnmute}
              onClick={handleUnmute}
              aria-label="Ativar áudio"
              className="absolute left-1/2 -translate-x-1/2 bottom-3 z-20 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-red hover:bg-brand-redHover text-white text-[11px] font-heading font-semibold shadow-lg border border-white/20 backdrop-blur-md transition-colors whitespace-nowrap"
            >
              <Volume2 className="w-4 h-4 shrink-0" />
              Ativar Áudio
            </button>
          )}
          <button
            type="button"
            className="absolute right-3 top-3 z-20 w-8 h-8 bg-brand-red/80 hover:bg-brand-red rounded-full flex items-center justify-center shrink-0 transition-colors"
            onClick={(e) => { e.stopPropagation(); handleTogglePlay(); }}
            aria-label="Pausar vídeo"
          >
            <Pause className="w-3 h-3 text-white" />
          </button>
        </>
      ) : (
        <>
          <div className="absolute inset-0 rounded-[inherit] overflow-hidden">
            {meta?.thumbnail ? (
              <img src={meta.thumbnail} alt={meta?.title} className="block w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" width={250} height={444} />
            ) : (
              <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                <Play className="w-6 h-6 text-white/30" />
              </div>
            )}
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/10 group-hover:via-black/40 transition-colors duration-500" />

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 bg-brand-red/90 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 transition-transform duration-300 group-hover:scale-110 shadow-[0_0_20px_rgba(220,38,38,0.5)]">
              <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
            </div>
          </div>

          <div className="absolute bottom-0 left-0 w-full p-5 pointer-events-none">
            <h3 className="font-heading font-bold text-white text-lg drop-shadow-md group-hover:-translate-y-1 transition-transform duration-300">
              {meta?.title || fallbackTitle || ""}
            </h3>
          </div>
        </>
      )}
    </div>
  );
}
